/* eslint-disable no-param-reassign */
/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Button from '@mui/material/Button'
import { parse } from 'query-string'
import moment from 'moment'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { withAITracking } from '@microsoft/applicationinsights-react-js'
import PagingControl from '../PagingControl'
import Header from '../Header'
import Loader from '../Loader'
import TimeFrameFilter from '../Filters/TimeFrameFilter'
import ApprovalStatusFilter from '../Filters/ApprovalStatusFilter'
import OccupationGroupFilter from '../Filters/OccupationGroupFilter'
import DomainNameFilter from '../Filters/DomainNameFilter'
import DomainInfoPopover from './DomainInfoPopover'
import DomainActionButton from './DomainActionButton'
import OccupationName from '../Occupations/OccupationName'
import {
  formatDateTime,
  formatDateTimeNoTimezoneAbbreviation,
  formatDateTimeWithoutTimezone,
  getTimeFrameTypeName,
} from '../../formatting'
import {
  createWhitelistEmailDomainSearch,
  updateWhitelistEmailDomainSearch,
  searchWhitelistEmailDomains,
  showModal,
  fetchPublishedOccupations,
} from '../../redux/actions'
import { createFilteredWhitelistEmailDomainsSelector, selectWhitelistEmailDomainSearch } from '../../redux/selectors'
import { reactPlugin } from '../../applicationInsights'

export const DEFAULT_SEARCH = {
  id: 'whitelist-queue',
  filter: {
    searchText: { text: '', expanded: false },
    timeFrame: {
      type: 'last7Days', from: null, to: null, expanded: false,
    },
    approvalStatuses: {
      statuses: [{ type: 'Approved', selected: true }, { type: 'Pending', selected: true }],
      expanded: false,
    },
    occupationGroups: {
      groups: [
        { type: 1, name: 'Military', selected: true },
        { type: 56, name: 'Military Family', selected: true },
        { type: 83, name: 'Law Enforcement', selected: true },
        { type: 132, name: 'Fire', selected: true },
        { type: 148, name: 'Emergency Medical Services', selected: true },
        { type: 151, name: 'Government Employee', selected: true },
        { type: 161, name: 'Internal User', selected: true },
        { type: 214, name: 'VCS', selected: true },
        { type: 219, name: 'Education', selected: true },
        { type: 229, name: 'Patriot', selected: true },
        { type: 292, name: 'Student', selected: true },
      ],
      expanded: false,
    },
  },
  paging: {
    pageSize: 25,
  },
}

const SEARCH_ID = 'whitelist-queue'

const getMostRecentCreatedDate = (domain) => {
  const updatedApproved = (
    domain.updatedOnUtc > domain.approvedOnUtc || domain.approvedOnUtc === null
  ) ? domain.updatedOnUtc : domain.approvedOnUtc
  const mostRecent = (
    updatedApproved > domain.createdOnUtc || domain.createdOnUtc === null
  ) ? updatedApproved : domain.createdOnUtc
  return formatDateTime(mostRecent)
}

const buildNewQueryString = (search, newPageSize) => {
  const {
    from, to, approvalStatuses, occupationGroups, domainName, pageSize, ...parsed
  } = parse(window.location.search)

  parsed.pageSize = newPageSize
    || (search.paging.pageSize ? search.paging.pageSize : (pageSize || DEFAULT_SEARCH.paging.pageSize))

  parsed.timeFrame = (search.filter.timeFrame && search.filter.timeFrame.type) ? search.filter.timeFrame.type : 'any'

  if (search.filter.timeFrame && search.filter.timeFrame.type === 'custom') {
    const { timeFrame } = search.filter

    parsed.from = timeFrame.from ? formatDateTimeWithoutTimezone(timeFrame.from) : ''
    parsed.to = timeFrame.to ? formatDateTimeWithoutTimezone(timeFrame.to) : ''
  } else {
    parsed.from = ''
    parsed.to = ''
  }

  parsed.approvalStatuses = (
    search.filter.approvalStatuses
    && search.filter.approvalStatuses.statuses
    && search.filter.approvalStatuses.statuses.length > 0
  )
    ? search.filter.approvalStatuses.statuses.filter((x) => x.selected).map((y) => y.type).join(' ')
    : ''

  parsed.occupationGroups = (
    search.filter.occupationGroups
    && search.filter.occupationGroups.groups
    && search.filter.occupationGroups.groups.length > 0
  )
    ? search.filter.occupationGroups.groups.filter((x) => x.selected).map((y) => y.name.replaceAll(' ', '_')).join(' ')
    : ''

  parsed.domainName = (search.filter.searchText && search.filter.searchText.text) ? search.filter.searchText.text : ''

  const newQuery = `?timeFrame=${parsed.timeFrame}&from=${encodeURI(parsed.from)}&to=${encodeURI(parsed.to)}&approvalStatuses=${parsed.approvalStatuses}&occupationGroups=${parsed.occupationGroups}&domainName=${parsed.domainName}&pageSize=${parsed.pageSize}`

  return newQuery
}

export function DomainWhitelist() {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [loaded, setLoaded] = useState(false)

  const search = useSelector(selectWhitelistEmailDomainSearch(SEARCH_ID))
  const whitelistedDomains = useSelector(createFilteredWhitelistEmailDomainsSelector(SEARCH_ID))

  const createSearch = () => dispatch(createWhitelistEmailDomainSearch(DEFAULT_SEARCH))
  const onPageSizeChange = (searchObj, pageSize) => {
    setLoaded(false)

    const newQuery = buildNewQueryString(searchObj, pageSize)

    return dispatch(
      updateWhitelistEmailDomainSearch({ ...searchObj, paging: { ...searchObj.paging, pageSize } }),
    ).then(() => dispatch(searchWhitelistEmailDomains(SEARCH_ID)).then(() => {
      navigate(newQuery)
      setLoaded(true)
    }))
  }
  const loadWhitelistedEmailDomains = () => {
    setLoaded(false)
    return dispatch(searchWhitelistEmailDomains(SEARCH_ID)).then(() => setLoaded(true))
  }
  const onUpdateSearchFilter = (searchEvent) => {
    setLoaded(false)

    const newQuery = buildNewQueryString(searchEvent, null)

    return dispatch(updateWhitelistEmailDomainSearch(searchEvent))
      .then(() => dispatch(searchWhitelistEmailDomains(SEARCH_ID)).then(() => {
        navigate(newQuery)
        setLoaded(true)
      }))
  }
  const onAddDomain = () => dispatch(showModal('ADD_WHITELISTED_DOMAIN'))
  const getPublishedOccupations = () => {
    setLoaded(false)
    return dispatch(fetchPublishedOccupations())
  }

  const [searchParams] = useSearchParams()

  useEffect(() => {
    getPublishedOccupations()

    const timeFrameType = getTimeFrameTypeName(searchParams.get('timeFrame'))

    if (timeFrameType === 'any' || timeFrameType === 'last7Days' || timeFrameType === 'last30Days') {
      DEFAULT_SEARCH.filter.timeFrame = {
        type: timeFrameType, from: null, to: null, expanded: false,
      }
    } else if (searchParams.get('from') || searchParams.get('to')) {
      const from = searchParams.get('from') ? moment(Date.parse(formatDateTimeNoTimezoneAbbreviation(searchParams.get('from')))) : null
      const to = searchParams.get('to') ? moment(Date.parse(formatDateTimeNoTimezoneAbbreviation(searchParams.get('to')))) : null

      DEFAULT_SEARCH.filter.timeFrame = {
        type: 'custom', from, to, expanded: false,
      }
    }

    if (searchParams.get('pageSize') && Number(searchParams.get('pageSize')) > 0) { DEFAULT_SEARCH.paging.pageSize = Number(searchParams.get('pageSize')) }

    if (searchParams.get('domainName')) { DEFAULT_SEARCH.filter.searchText.text = searchParams.get('domainName') }

    if (searchParams.get('approvalStatuses')) {
      const statuses = searchParams.get('approvalStatuses').toLowerCase().split(' ')

      DEFAULT_SEARCH.filter.approvalStatuses.statuses.forEach((s) => {
        s.selected = statuses.includes(s.type.toLowerCase())
      })
    }

    if (searchParams.get('occupationGroups')) {
      const groups = searchParams.get('occupationGroups').toLowerCase().split(' ')

      DEFAULT_SEARCH.filter.occupationGroups.groups.forEach((s) => {
        s.selected = groups.includes(s.name.replaceAll(' ', '_').toLowerCase())
      })
    }

    createSearch().then(() => loadWhitelistedEmailDomains())
  }, [])

  if (!loaded) return <Loader />
  return (
    <div style={{ minWidth: 'min-content' }}>
      <Header title="Domain Whitelist">
        <Button label="Add domain" variant="contained" color="primary" onClick={onAddDomain}>
          Add domain
        </Button>
      </Header>
      <div style={{ display: 'flex' }}>
        <div style={{ flex: 1, marginRight: '10px' }}>
          <TimeFrameFilter search={search} onUpdate={onUpdateSearchFilter} anchor="timeFilterAnchor" />
          <div id="timeFilterAnchor" />
        </div>
        <div style={{ flex: 1, margin: '0px 10px' }}>
          <ApprovalStatusFilter search={search} onUpdate={onUpdateSearchFilter} anchor="approvalStatusFilter" />
          <div id="approvalStatusFilter" />
        </div>
        <div style={{ flex: 1, marginLeft: '10px' }}>
          <DomainNameFilter search={search} onUpdate={onUpdateSearchFilter} anchor="domainNameFilter" />
          <div id="domainNameFilter" />
        </div>
      </div>
      <div style={{ display: 'flex', marginTop: '20px' }}>
        <div style={{ flex: 1, marginRight: '10px' }}>
          <OccupationGroupFilter search={search} onUpdate={onUpdateSearchFilter} anchor="occupationGroupFilter" />
          <div id="occupationGroupFilter" />
        </div>
        <div style={{ flex: 1, margin: '0px 10px' }} />
        <div style={{ flex: 1, marginLeft: '10px' }} />
      </div>

      <PagingControl
        pageSize={search.paging.pageSize}
        total={search.total}
        onChange={(pageSize) => onPageSizeChange(search, pageSize)}
      />

      {search.total > 0 ? (
        <Table>
          <TableHead style={{ backgroundColor: '#f1f1f1' }}>
            <TableRow>
              <TableCell style={{
                fontSize: '14px', color: '#222', fontWeight: '600', width: '35%',
              }}
              >
                Domain
              </TableCell>
              <TableCell style={{
                fontSize: '14px', color: '#222', fontWeight: '600', width: '25%',
              }}
              >
                Affiliation
              </TableCell>
              <TableCell style={{
                fontSize: '14px', color: '#222', fontWeight: '600', width: '15%',
              }}
              >
                Updated
              </TableCell>
              <TableCell style={{
                fontSize: '14px', color: '#222', fontWeight: '600', width: '5%',
              }}
              >
                Status
              </TableCell>
              <TableCell style={{
                fontSize: '14px', color: '#222', fontWeight: '600', cursor: 'default', width: '20%',
              }}
              >
            &nbsp;
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {whitelistedDomains
              .sort((a, b) => new Date(getMostRecentCreatedDate(b)) - new Date(getMostRecentCreatedDate(a)))
              .map((d, i) => (
                <TableRow key={i}>
                  <TableCell style={{ position: 'relative' }}>
                    <DomainInfoPopover email={d.name} domainName={d.name} />
                  </TableCell>
                  <TableCell style={{ position: 'relative' }}>
                    <OccupationName occupationId={d.occupationId} />
                  </TableCell>
                  <TableCell style={{ position: 'relative' }}>
                    {getMostRecentCreatedDate(d)}
                  </TableCell>
                  <TableCell style={{ position: 'relative' }}>
                    {d.approvalStatus}
                  </TableCell>
                  <TableCell style={{ position: 'relative', textAlign: 'center', padding: '0px' }}>
                    <DomainActionButton domain={d} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      ) : (
        <h2 style={{ marginTop: '50px', fontSize: '1.5em', fontWeight: 'bold' }}>No results</h2>
      )}

      <PagingControl
        pageSize={search.paging.pageSize}
        total={search.total}
        isBottom
        onChange={(pageSize) => onPageSizeChange(search, pageSize)}
      />
    </div>
  )
}

export default withAITracking(reactPlugin, DomainWhitelist)
